import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AuthShell, ErrorBox, GoogleIcon } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, ArrowRight, Globe, CheckCircle2 } from "lucide-react";

const PENDING_KEY = "blinxd_pending_site";

export default function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [siteUrl, setSiteUrl] = useState("");
  const [niche, setNiche] = useState("");
  const [niches, setNiches] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/niches").then(({ data }) => setNiches(data.niches)).catch(() => {});
  }, []);

  const continueToStep2 = async (e) => {
    e.preventDefault();
    setError("");
    if (!niche) { setError("Please pick your site's niche."); return; }
    setBusy(true);
    try {
      await api.post("/auth/check-site", { url: siteUrl });
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, site_url: siteUrl, niche });
      setUser(data);
      nav(data.plan_chosen ? "/dashboard" : "/pricing");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // Stash the site the user entered so we can attach it after the OAuth round-trip.
    localStorage.setItem(PENDING_KEY, JSON.stringify({ site_url: siteUrl, niche }));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Join the pool" subtitle="Add your site first — then create your account.">
      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-6" data-testid="register-steps">
        <StepDot n={1} active={step === 1} done={step > 1} label="Your site" />
        <div className="h-px flex-1 bg-border" />
        <StepDot n={2} active={step === 2} done={false} label="Account" />
      </div>

      {step === 1 ? (
        <form onSubmit={continueToStep2} className="space-y-4" data-testid="register-step-1">
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="space-y-2">
            <Label htmlFor="siteUrl">Your website URL</Label>
            <div className="relative">
              <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="siteUrl" required value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)}
                data-testid="register-site-url" className="rounded-xl bg-white border-stone-300 h-11 pl-9 font-mono" placeholder="https://yoursite.com" />
            </div>
            <p className="text-[11px] text-muted-foreground">This is the site you want to swap backlinks for. Major brand domains aren't eligible.</p>
          </div>
          <div className="space-y-2">
            <Label>Primary niche</Label>
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger data-testid="register-niche-select" className="rounded-xl bg-white border-stone-300 h-11">
                <SelectValue placeholder="Select a niche" />
              </SelectTrigger>
              <SelectContent className="rounded-lg max-h-72">
                {niches.map((n) => (<SelectItem key={n} value={n} data-testid={`register-niche-${n}`}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} data-testid="register-continue"
            className="w-full rounded-full bg-terracotta text-white hover:bg-terracotta-dark h-11 active:scale-95 transition-transform">
            {busy ? "Checking site…" : (<>Continue <ArrowRight size={16} className="ml-1.5" /></>)}
          </Button>
        </form>
      ) : (
        <div data-testid="register-step-2">
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2.5 text-sm">
            <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
            <span className="text-muted-foreground truncate">
              <span className="font-mono text-foreground">{siteUrl.replace(/^https?:\/\//, "")}</span> · {niche}
            </span>
            <button onClick={() => { setStep(1); setError(""); }} data-testid="register-edit-site"
              className="ml-auto text-terracotta text-xs font-medium hover:underline flex items-center gap-1">
              <ArrowLeft size={12} /> Edit
            </button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {error && <ErrorBox>{error}</ErrorBox>}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name" className="rounded-xl bg-white border-stone-300 h-11" placeholder="Jane Doe" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email" className="rounded-xl bg-white border-stone-300 h-11" placeholder="you@site.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password" className="rounded-xl bg-white border-stone-300 h-11" placeholder="min 6 characters" />
            </div>
            <Button type="submit" disabled={busy} data-testid="register-submit"
              className="w-full rounded-full bg-terracotta text-white hover:bg-terracotta-dark h-11 active:scale-95 transition-transform">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-muted-foreground">
            <div className="h-px flex-1 bg-border" /><span className="font-mono text-[10px] tracking-widest">OR</span><div className="h-px flex-1 bg-border" />
          </div>
          <Button type="button" onClick={handleGoogle} variant="outline" data-testid="google-register-button"
            className="w-full rounded-full border-stone-300 bg-white hover:bg-muted h-11 font-medium">
            <GoogleIcon /> <span className="ml-2">Continue with Google</span>
          </Button>
        </div>
      )}

      <p className="mt-6 text-sm text-muted-foreground text-center">
        Already have an account? <Link to="/login" className="text-terracotta font-medium hover:underline" data-testid="to-login">Log in</Link>
      </p>
    </AuthShell>
  );
}

function StepDot({ n, active, done, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-mono font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-terracotta text-white" : "bg-muted text-muted-foreground"}`}>
        {done ? "✓" : n}
      </div>
      <span className={`text-xs font-medium ${active || done ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}
